import type { TreeNode } from '../../service/tree.service';

type TreeIndex = Record<number, TreeNode>;

/** Flatten every node of the nested tree by id. */
export const indexTree = (roots: TreeNode[]): TreeIndex => {
  const index: TreeIndex = {};
  const visit = (node: TreeNode) => {
    index[node.id] = node;
    (node.children ?? []).forEach(visit);
  };
  (roots ?? []).forEach(visit);
  return index;
};

/**
 * Root-first chain ending at the node itself. Follows parent_id only while the parent
 * is in the index: an unreachable parent makes the node top-level, never a dangling link.
 */
export const ancestorsOf = (index: TreeIndex, id: number): TreeNode[] => {
  const chain: TreeNode[] = [];
  const seen = new Set<number>();
  let current: TreeNode | undefined = index[id];
  while (current && !seen.has(current.id)) {
    seen.add(current.id);
    chain.unshift(current);
    current = current.parent_id != null ? index[current.parent_id] : undefined;
  }
  return chain;
};

export const childrenOf = (index: TreeIndex, id: number): TreeNode[] =>
  index[id]?.children ?? [];

/** Other nodes under the same parent; for a top-level node, the other roots of its kind. */
export const siblingsOf = (index: TreeIndex, roots: TreeNode[], id: number): TreeNode[] => {
  const node = index[id];
  if (!node) return [];
  const parent = node.parent_id != null ? index[node.parent_id] : undefined;
  const pool = parent ? parent.children : roots.filter((r) => r.kind === node.kind);
  return pool.filter((n) => n.id !== id);
};

/** Depth-first list with depth, for an indented switcher. */
export const flattenForSwitcher = (roots: TreeNode[]): { node: TreeNode; depth: number }[] => {
  const out: { node: TreeNode; depth: number }[] = [];
  const visit = (node: TreeNode, depth: number) => {
    out.push({ node, depth });
    (node.children ?? []).forEach((c) => visit(c, depth + 1));
  };
  (roots ?? []).forEach((r) => visit(r, 0));
  return out;
};

/** Console route for a node; workspaces have none. */
export const consolePathFor = (node: TreeNode): string | null => {
  if (node.kind === 'organisation') return `/org/${node.id}`;
  if (node.kind === 'entity') return `/entity/${node.id}`;
  return null;
};

export interface AccessShape {
  shape: 'org' | 'entities' | 'workspaces';
  entityCount: number;
  workspaceCount: number;
}

/** What the caller's tree roots look like: org access, entity-only access, or workspaces only. */
export const accessShapeOf = (roots: TreeNode[]): AccessShape => {
  const all = flattenForSwitcher(roots ?? []);
  const entityCount = all.filter((r) => r.node.kind === 'entity').length;
  const workspaceCount = all.filter((r) => r.node.kind === 'workspace').length;
  let shape: AccessShape['shape'] = 'workspaces';
  if ((roots ?? []).some((r) => r.kind === 'organisation')) shape = 'org';
  else if ((roots ?? []).some((r) => r.kind === 'entity')) shape = 'entities';
  return { shape, entityCount, workspaceCount };
};
